import { useCallback, useEffect, useMemo, useState } from 'react'

import { FormField, formInputProps } from '../../../components/ui/FormField'
import { Modal, ModalActions } from '../../../components/ui/Modal'
import { pb } from '../../../pocketbase'
import { buildChildrenByParent, collectSubtreeIds, type CategoryRow } from '../../../lib/categoryTree'
import { alert as alertClass, btn, categoryTreeAsideClass, categoryTreeChipClass, cn, formGridClass, inputClass } from '../../../lib/ui'
import { pocketbaseErrorMessage } from '../../../lib/pocketbaseErrorMessage'

export type ProductionCategoryFilter =
  | { kind: 'all' }
  | { kind: 'uncategorized' }
  | { kind: 'subtree'; rootId: string; ids: string[] }

type CategoryRowSource = { id: string; name: string; parent?: string; sort?: number }

type Props = {
  /** Коллекция PocketBase: consumable_categories или product_categories */
  categoryCollection: string
  /** Заголовок панели */
  title?: string
  value: ProductionCategoryFilter
  onChange: (next: ProductionCategoryFilter) => void
  canManage: boolean
}

function rowsFromRecords(list: CategoryRowSource[]): CategoryRow[] {
  return list.map((c) => ({
    id: c.id,
    name: c.name,
    parent: typeof c.parent === 'string' ? c.parent : '',
    sort: typeof c.sort === 'number' ? c.sort : 0,
  }))
}

function TreeBranch({
  parentKey,
  depth,
  byParent,
  selectedRootId,
  onPick,
}: {
  parentKey: string
  depth: number
  byParent: Map<string, CategoryRow[]>
  selectedRootId: string | null
  onPick: (id: string) => void
}) {
  const nodes = byParent.get(parentKey) ?? []
  return (
    <ul className={cn('m-0 list-none p-0', depth > 0 && 'ml-1 border-l border-su-border pl-1')}>
      {nodes.map((n) => (
        <li key={n.id}>
          <div>
            <button
              type="button"
              className={cn(
                'block w-full rounded-lg border-none bg-transparent py-1 pr-2 text-left text-[0.8125rem] font-medium text-su-text-strong transition hover:bg-su-surface-muted',
                selectedRootId === n.id && 'bg-indigo-500/10 font-semibold text-su-primary',
              )}
              style={{ paddingLeft: `${8 + depth * 14}px` }}
              onClick={() => onPick(n.id)}
            >
              {n.name}
            </button>
          </div>
          <TreeBranch
            parentKey={n.id}
            depth={depth + 1}
            byParent={byParent}
            selectedRootId={selectedRootId}
            onPick={onPick}
          />
        </li>
      ))}
    </ul>
  )
}

export function CategoryTreePanel({
  categoryCollection,
  title = 'Категории',
  value,
  onChange,
  canManage,
}: Props) {
  const [rows, setRows] = useState<CategoryRow[]>([])
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(false)
  const [newName, setNewName] = useState('')
  const [newParent, setNewParent] = useState('')

  const load = useCallback(async () => {
    setErr('')
    setLoading(true)
    try {
      const list = await pb.collection(categoryCollection).getFullList<CategoryRowSource>({
        sort: 'sort,name',
      })
      setRows(rowsFromRecords(list))
    } catch (e: unknown) {
      setErr(pocketbaseErrorMessage(e, 'Ошибка загрузки категорий'))
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [categoryCollection])

  useEffect(() => {
    void load()
  }, [load])

  const byParent = useMemo(() => buildChildrenByParent(rows), [rows])

  const flatOptions = useMemo(() => {
    const out: { id: string; label: string }[] = []
    const walk = (parentKey: string, depth: number) => {
      for (const n of byParent.get(parentKey) ?? []) {
        out.push({ id: n.id, label: `${'—'.repeat(depth)} ${n.name}`.trim() })
        walk(n.id, depth + 1)
      }
    }
    walk('', 0)
    return out
  }, [byParent])

  const selectedRootId = value.kind === 'subtree' ? value.rootId : null

  async function createCategory(e: React.FormEvent) {
    e.preventDefault()
    const name = newName.trim()
    if (!name) return
    try {
      const maxSort = rows.reduce((m, r) => Math.max(m, Number(r.sort) || 0), 0)
      const payload: Record<string, unknown> = { name, sort: maxSort + 1 }
      if (newParent) payload.parent = newParent
      await pb.collection(categoryCollection).create(payload)
      setModal(false)
      setNewName('')
      setNewParent('')
      await load()
    } catch (e: unknown) {
      alert(pocketbaseErrorMessage(e, 'Ошибка'))
    }
  }

  return (
    <aside className={categoryTreeAsideClass} aria-label={title}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <strong className="text-sm text-su-text-strong">{title}</strong>
        {canManage && (
          <button type="button" className={btn('primary', 'sm')} onClick={() => setModal(true)}>
            + Новая
          </button>
        )}
      </div>
      {err && <div className={cn(alertClass('err'), 'mb-2')}>{err}</div>}
      {loading && <p className="m-0 text-sm text-su-text">Загрузка…</p>}
      {!loading && (
        <>
          <div className="mb-2 flex flex-wrap gap-1.5">
            <button
              type="button"
              className={categoryTreeChipClass(value.kind === 'all')}
              onClick={() => onChange({ kind: 'all' })}
            >
              Все
            </button>
            <button
              type="button"
              className={categoryTreeChipClass(value.kind === 'uncategorized')}
              onClick={() => onChange({ kind: 'uncategorized' })}
            >
              Без категории
            </button>
          </div>
          {rows.length === 0 ? (
            <p className="m-0 text-sm text-su-text">Категорий пока нет — создайте первую.</p>
          ) : (
            <TreeBranch
              parentKey=""
              depth={0}
              byParent={byParent}
              selectedRootId={selectedRootId}
              onPick={(id) => onChange({ kind: 'subtree', rootId: id, ids: collectSubtreeIds(rows, id) })}
            />
          )}
        </>
      )}

      {modal && (
        <Modal title="Новая категория" titleId="cat-modal-title" onClose={() => setModal(false)}>
          <form onSubmit={(e) => void createCategory(e)} className={formGridClass}>
            <FormField label="Название" htmlFor="cat-new-name">
              <input
                id="cat-new-name"
                {...formInputProps()}
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={200}
                required
                autoFocus
              />
            </FormField>
            <FormField label="Вложить в" htmlFor="cat-new-parent">
              <select
                id="cat-new-parent"
                className={inputClass}
                value={newParent}
                onChange={(e) => setNewParent(e.target.value)}
              >
                <option value="">Корень дерева</option>
                {flatOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FormField>
            <ModalActions>
              <button type="submit" className={btn('primary')}>
                Создать
              </button>
              <button type="button" className={btn('ghost')} onClick={() => setModal(false)}>
                Отмена
              </button>
            </ModalActions>
          </form>
        </Modal>
      )}
    </aside>
  )
}
